import React, { useState, useRef, useEffect } from 'react';
import {
  MdMenu,
  MdSearch,
  MdMic,
  MdAdd,
  MdOutlineNotifications,
  MdMoreVert,
  MdLogout,
  MdSettings,
  MdOutlineHelpOutline,
  MdOutlineDarkMode,
  MdTranslate,
  MdChevronRight,
  MdOutlineLibraryMusic,
  MdOutlineAccountBox,
  MdArrowBack,
  MdCheck,
  MdOutlineVideocam,
  MdOutlineSlowMotionVideo,
  MdOutlineAlbum,
  MdSwitchAccount,
  MdClose,
} from 'react-icons/md';
import { HiOutlineUserCircle } from 'react-icons/hi2';
import logoDarkPng from './assets/speedotube_logo_2ddark.png';
import logoLightPng from './assets/speedotube_logo_2dlight.png';
import { DISPLAY_LANGUAGES, applyGlobalTranslation, getTranslation } from './translations';
import { getAvatarColor } from './utils/avatarUtils';
import { getActiveAccount, AVAILABLE_ACCOUNTS, getAvailableAccounts, setActiveAccount } from './utils/accountUtils';
import { API_BASE_URL, normalizeMediaUrl } from './config';
import VoiceSearchModal from './VoiceSearchModal';
import ConfirmModal from './ConfirmModal';
import './Header.css';

export default function Header({ onToggleSidebar, isLoggedIn = true, onAuthChange, userProfile, onNavigate, activePath }) {
  const [searchQuery, setSearchQuery] = useState('');
  const [suggestions, setSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [selectedSuggestionIndex, setSelectedSuggestionIndex] = useState(-1);
  const searchBoxRef = useRef(null);
  const searchInputRef = useRef(null);
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [showCreateMenu, setShowCreateMenu] = useState(false);
  const [showMoreMenu, setShowMoreMenu] = useState(false);
  const [menuView, setMenuView] = useState('main'); // 'main' | 'language'
  const [isVoiceModalOpen, setIsVoiceModalOpen] = useState(false);
  const [showLogoutModal, setShowLogoutModal] = useState(false);
  const [isMobileSearchOpen, setIsMobileSearchOpen] = useState(false);

  const [selectedLang, setSelectedLang] = useState(() => {
    try {
      const saved = localStorage.getItem('speedotube_display_language');
      if (saved) return JSON.parse(saved);
    } catch (e) {}
    return { code: 'en', name: 'English' };
  });

  const [theme, setTheme] = useState(() => {
    try {
      const q = new URLSearchParams(window.location.search).get('theme');
      if (q) return q;
    } catch (e) {}
    return localStorage.getItem('speedotube_theme') || 'dark';
  });

  const applyThemeMode = (mode) => {
    setTheme(mode);
    localStorage.setItem('speedotube_theme', mode);
    
    if (mode === 'dark') {
      document.body.classList.add('dark-theme');
      document.documentElement.classList.add('dark-theme');
      document.body.classList.remove('light-theme');
      document.documentElement.classList.remove('light-theme');
      document.documentElement.setAttribute('data-theme', 'dark');
    } else {
      document.body.classList.remove('dark-theme');
      document.documentElement.classList.remove('dark-theme');
      document.body.classList.add('light-theme');
      document.documentElement.classList.add('light-theme');
      document.documentElement.setAttribute('data-theme', 'light');
    }
  };

  useEffect(() => {
    applyThemeMode(theme);
  }, []);

  const userMenuRef = useRef(null);
  const createMenuRef = useRef(null);
  const moreMenuRef = useRef(null);

  const activeAcc = getActiveAccount();
  const profileName = userProfile?.name || activeAcc?.name || activeAcc?.fullName || 'Speedo Code';
  const profileHandle = userProfile?.handle || activeAcc?.handle || `@${profileName.toLowerCase().replace(/[^a-z0-9]/gi, '')}`;
  const profileInitial = (profileName && profileName.trim()) ? profileName.trim().charAt(0).toUpperCase() : (userProfile?.initial || activeAcc?.initial || 'U');
  const avatarColor = getAvatarColor(profileName || profileHandle || profileInitial);

  const [liveAvatar, setLiveAvatar] = useState(() => {
    return userProfile?.avatar_url || userProfile?.avatarUrl || userProfile?.profile_image || activeAcc?.avatar_url || activeAcc?.profile_image || '';
  });
  const [imgError, setImgError] = useState(false);

  useEffect(() => {
    const nextUrl = userProfile?.avatar_url || userProfile?.avatarUrl || userProfile?.profile_image || '';
    if (nextUrl) {
      setLiveAvatar(nextUrl);
      setImgError(false);
    }
  }, [userProfile]);

  useEffect(() => {
    const handleAccountSwitched = (e) => {
      const switchedAccount = e.detail;
      if (switchedAccount) {
        const nextUrl = switchedAccount?.avatar_url || switchedAccount?.avatarUrl || switchedAccount?.profile_image || '';
        setLiveAvatar(nextUrl);
        setImgError(false);
      }
    };
    const handleChannelUpdated = (e) => {
      const updatedChannel = e.detail;
      if (updatedChannel) {
        const nextUrl = updatedChannel?.avatar_url || updatedChannel?.avatarUrl || updatedChannel?.profile_image || '';
        setLiveAvatar(nextUrl);
        setImgError(false);
      }
    };
    window.addEventListener('speedotube_user_switched', handleAccountSwitched);
    window.addEventListener('speedotube_channel_updated', handleChannelUpdated);
    return () => {
      window.removeEventListener('speedotube_user_switched', handleAccountSwitched);
      window.removeEventListener('speedotube_channel_updated', handleChannelUpdated);
    };
  }, []);

  const getResolvedAvatar = () => {
    let raw = '';
    if (liveAvatar) raw = liveAvatar;
    else if (userProfile?.avatar_url || userProfile?.avatarUrl || userProfile?.profile_image) {
      raw = userProfile.avatar_url || userProfile.avatarUrl || userProfile.profile_image;
    } else {
      const acc = getActiveAccount();
      if (acc?.avatar_url || acc?.profile_image) {
        raw = acc.avatar_url || acc.profile_image;
      } else {
        try {
          const h = profileHandle.toLowerCase();
          const customRaw = localStorage.getItem(`speedotube_customization_${h}`);
          if (customRaw) {
            const custom = JSON.parse(customRaw);
            if (custom?.avatar_url || custom?.avatar_filename) raw = custom.avatar_url || custom.avatar_filename;
          }
        } catch (e) {}
      }
    }
    return normalizeMediaUrl(raw);
  };

  const avatarUrl = getResolvedAvatar();

  // Apply language on load if saved
  useEffect(() => {
    if (selectedLang && selectedLang.code) {
      applyGlobalTranslation(selectedLang.code);
    }
  }, []);

  // Close dropdown menus and search suggestions when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (userMenuRef.current && !userMenuRef.current.contains(event.target)) {
        setShowUserMenu(false);
        setMenuView('main');
      }
      if (createMenuRef.current && !createMenuRef.current.contains(event.target)) {
        setShowCreateMenu(false);
      }
      if (moreMenuRef.current && !moreMenuRef.current.contains(event.target)) {
        setShowMoreMenu(false);
        setMenuView('main');
      }
      if (searchBoxRef.current && !searchBoxRef.current.contains(event.target)) {
        setShowSuggestions(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Live autocomplete / search suggestions fetcher as user types
  useEffect(() => {
    const trimmed = searchQuery.trim();
    if (!trimmed) {
      setSuggestions([]);
      setShowSuggestions(false);
      setSelectedSuggestionIndex(-1);
      return;
    }

    const timer = setTimeout(async () => {
      try {
        const res = await fetch(
          `${API_BASE_URL}/api/v1/search/options?query=${encodeURIComponent(trimmed)}&limit=10`,
          {
            headers: {
              token: 'c7e993808a344da9d59cfa20fa8b6061',
              devicetype: '0',
              request_type: 'USER',
            },
          }
        );

        let backendOptions = [];
        if (res.ok) {
          const json = await res.json();
          const raw = json?.data?.options || [];
          backendOptions = raw.map((item) => (typeof item === 'string' ? item : item.text || item.title)).filter(Boolean);
        }

        // Also search locally created videos from localStorage
        let localOptions = [];
        try {
          for (let i = 0; i < localStorage.length; i++) {
            const key = localStorage.key(i);
            if (key && (key.startsWith('speedotube_channel_videos_') || key === 'speedotube_cached_videos')) {
              if (key.toLowerCase().includes('aukat')) {
                localStorage.removeItem(key);
                continue;
              }
              const items = JSON.parse(localStorage.getItem(key) || '[]');
              if (Array.isArray(items)) {
                items.forEach((item) => {
                  const ch = (item?.channelName || item?.channelHandle || item?.handleName || '').toLowerCase();
                  if (ch.includes('aukat')) return;
                  if (item?.title && item.title.toLowerCase().includes(trimmed.toLowerCase())) {
                    localOptions.push(item.title);
                  }
                });
              }
            }
          }
        } catch (e) {}

        // Deduplicate
        const seen = new Set();
        const combined = [];
        for (const text of [...backendOptions, ...localOptions]) {
          const lower = text.toLowerCase().trim();
          if (!seen.has(lower) && lower.length > 0) {
            seen.add(lower);
            combined.push(text.trim());
          }
        }

        setSuggestions(combined.slice(0, 10));
        setShowSuggestions(combined.length > 0);
        setSelectedSuggestionIndex(-1);
      } catch (err) {
        console.warn('Suggestions fetch error:', err);
      }
    }, 150);

    return () => clearTimeout(timer);
  }, [searchQuery]);

  useEffect(() => {
    if (activePath && activePath.startsWith('/results')) {
      const searchStr = (activePath.includes('?') ? activePath.substring(activePath.indexOf('?')) : '') || window.location.search || '';
      const q = new URLSearchParams(searchStr).get('search_query');
      if (q && q !== searchQuery) {
        setSearchQuery(q);
      }
    }
  }, [activePath]);

  const executeSearch = (rawQuery) => {
    const query = (rawQuery !== undefined ? rawQuery : searchQuery || '').trim();
    if (!query) return;
    setShowSuggestions(false);
    setSelectedSuggestionIndex(-1);
    if (searchInputRef.current) {
      try {
        searchInputRef.current.blur();
      } catch (e) {}
    }

    if (activePath === '/history') {
      window.dispatchEvent(new CustomEvent('speedotube_search_history', { detail: query }));
      return;
    }

    const searchUrl = `/results?search_query=${encodeURIComponent(query)}`;
    if (onNavigate) {
      onNavigate(searchUrl);
    } else {
      window.history.pushState({}, '', searchUrl);
      window.dispatchEvent(new CustomEvent('speedotube_navigate', { detail: { path: searchUrl } }));
    }
  };

  const handleSearchKeyDown = (e) => {
    if (!showSuggestions || suggestions.length === 0) {
      if (e.key === 'Enter') {
        handleSearchSubmit(e);
      }
      return;
    }

    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setSelectedSuggestionIndex((prev) => {
        const next = prev < suggestions.length - 1 ? prev + 1 : 0;
        setSearchQuery(suggestions[next]);
        return next;
      });
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setSelectedSuggestionIndex((prev) => {
        const next = prev > 0 ? prev - 1 : suggestions.length - 1;
        setSearchQuery(suggestions[next]);
        return next;
      });
    } else if (e.key === 'Escape') {
      setShowSuggestions(false);
      setSelectedSuggestionIndex(-1);
    } else if (e.key === 'Enter') {
      e.preventDefault();
      if (selectedSuggestionIndex >= 0 && suggestions[selectedSuggestionIndex]) {
        const chosen = suggestions[selectedSuggestionIndex];
        setSearchQuery(chosen);
        executeSearch(chosen);
      } else {
        executeSearch(searchQuery);
      }
    }
  };

  const handleSearchSubmit = (e) => {
    if (e && e.preventDefault) e.preventDefault();
    setIsMobileSearchOpen(false);
    executeSearch(searchQuery);
  };

  const handleLogout = () => {
    setShowUserMenu(false);
    setMenuView('main');
    setShowLogoutModal(true);
  };

  const handleConfirmLogout = () => {
    setShowLogoutModal(false);
    try {
      window.dispatchEvent(new CustomEvent('speedotube_reset_music_session'));
    } catch (e) {}
    if (onAuthChange) onAuthChange(false);
  };

  const handleLogin = () => {
    setShowMoreMenu(false);
    setMenuView('main');
    if (onAuthChange) onAuthChange(true);
  };

  const handleLanguageSelect = (lang) => {
    setSelectedLang(lang);
    localStorage.setItem('speedotube_display_language', JSON.stringify(lang));
    applyGlobalTranslation(lang.code);
    setMenuView('main');
    setShowUserMenu(false);
    setShowMoreMenu(false);
    setTimeout(() => {
      window.location.reload();
    }, 150);
  };

  const renderAppearanceSubmenu = () => (
    <div className="yt-language-submenu">
      <div className="yt-language-header">
        <button
          type="button"
          className="yt-language-back-btn"
          onClick={() => setMenuView('main')}
          title="Back"
        >
          <MdArrowBack size={20} />
        </button>
        <span className="yt-language-title">Appearance</span>
      </div>
      <div className="yt-language-subtitle">Setting applies to this browser only</div>
      <hr className="yt-dropdown-divider" style={{ margin: '6px 0' }} />
      <div className="yt-language-list">
        <button
          type="button"
          className={`yt-language-item ${theme === 'dark' ? 'selected' : ''}`}
          onClick={() => applyThemeMode('dark')}
        >
          <div className="yt-language-check-wrapper">
            {theme === 'dark' && <MdCheck size={20} className="yt-language-check" />}
          </div>
          <span className="yt-language-name">Dark theme</span>
        </button>

        <button
          type="button"
          className={`yt-language-item ${theme === 'light' ? 'selected' : ''}`}
          onClick={() => applyThemeMode('light')}
        >
          <div className="yt-language-check-wrapper">
            {theme === 'light' && <MdCheck size={20} className="yt-language-check" />}
          </div>
          <span className="yt-language-name">Light theme</span>
        </button>
      </div>
    </div>
  );

  const renderLanguageSubmenu = () => (
    <div className="yt-language-submenu">
      <div className="yt-language-header">
        <button
          type="button"
          className="yt-language-back-btn"
          onClick={() => setMenuView('main')}
          title="Back"
        >
          <MdArrowBack size={20} />
        </button>
        <span className="yt-language-title">Display language</span>
      </div>
      <div className="yt-language-subtitle">Buttons and display text on this browser</div>
      <hr className="yt-dropdown-divider" style={{ margin: '6px 0' }} />
      <div className="yt-language-list">
        {DISPLAY_LANGUAGES.map((lang) => {
          const isSelected = selectedLang?.code === lang.code;
          return (
            <button
              key={lang.code}
              type="button"
              className={`yt-language-item ${isSelected ? 'selected' : ''}`}
              onClick={() => handleLanguageSelect(lang)}
            >
              <div className="yt-language-check-wrapper">
                {isSelected && <MdCheck size={20} className="yt-language-check" />}
              </div>
              <span className="yt-language-name">{lang.name}</span>
            </button>
          );
        })}
      </div>
    </div>
  );

  const renderSwitchAccountSubmenu = () => {
    const currentAcc = userProfile || getActiveAccount();
    return (
      <div className="yt-language-submenu">
        <div className="yt-language-header">
          <button
            type="button"
            className="yt-language-back-btn"
            onClick={() => setMenuView('main')}
            title="Back"
          >
            <MdArrowBack size={20} />
          </button>
          <span className="yt-language-title">Switch account</span>
        </div>
        <hr className="yt-dropdown-divider" style={{ margin: '6px 0' }} />
        <div className="yt-language-list">
          {(typeof getAvailableAccounts === 'function' ? getAvailableAccounts() : AVAILABLE_ACCOUNTS).map((acc) => {
            const isSelected =
              (currentAcc?.userId && (acc.userId === currentAcc.userId || acc.legacyId === currentAcc.userId)) ||
              (currentAcc?.id && (acc.id === currentAcc.id || acc.legacyId === currentAcc.id)) ||
              (currentAcc?.handle && acc.handle?.toLowerCase() === currentAcc.handle?.toLowerCase()) ||
              (currentAcc?.name && acc.name?.toLowerCase() === currentAcc.name?.toLowerCase());

            const initial = acc.initial || acc.name?.charAt(0)?.toUpperCase() || 'U';

            return (
              <button
                key={acc.id}
                type="button"
                className={`yt-dropdown-item yt-account-item ${isSelected ? 'selected' : ''}`}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  padding: '10px 16px',
                  width: '100%',
                  background: isSelected ? 'var(--yt-spec-badge-chip-background, rgba(0,0,0,0.05))' : 'transparent',
                  border: 'none',
                  cursor: 'pointer',
                  textAlign: 'left',
                }}
                onClick={() => {
                  setActiveAccount(acc);
                  setShowUserMenu(false);
                  setMenuView('main');
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                  <div
                    style={{
                      width: '32px',
                      height: '32px',
                      borderRadius: '50%',
                      backgroundColor: acc.avatarBg || '#8a3ffc',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      color: '#fff',
                      fontWeight: 600,
                      fontSize: '14px',
                      flexShrink: 0,
                    }}
                  >
                    {acc.avatar_url ? (
                      <img
                        src={normalizeMediaUrl(acc.avatar_url)}
                        alt={acc.name}
                        style={{ width: '100%', height: '100%', borderRadius: '50%', objectFit: 'cover' }}
                        onError={(e) => {
                          e.currentTarget.style.display = 'none';
                        }}
                      />
                    ) : (
                      initial
                    )}
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'column' }}>
                    <span style={{ fontSize: '14px', fontWeight: isSelected ? 600 : 500, color: 'inherit' }}>
                      {acc.name}
                    </span>
                    <span style={{ fontSize: '12px', color: 'var(--yt-spec-text-secondary, #888)' }}>
                      {acc.handle}
                    </span>
                  </div>
                </div>
                {isSelected && <MdCheck size={20} className="yt-check-icon" />}
              </button>
            );
          })}
        </div>
      </div>
    );
  };

  return (
    <header className="yt-header" style={{ borderBottom: 'none' }}>
      {/* Mobile Search Active Bar (Full-Width on Small Screens) */}
      {isMobileSearchOpen ? (
        <div className="yt-mobile-search-bar-full">
          <button
            type="button"
            className="yt-icon-btn yt-mobile-search-back-btn"
            onClick={() => setIsMobileSearchOpen(false)}
            aria-label="Back"
          >
            <MdArrowBack size={22} />
          </button>
          <form className="yt-search-container yt-mobile-search-form" onSubmit={handleSearchSubmit}>
            <div className="yt-search-box" ref={searchBoxRef}>
              <input
                ref={searchInputRef}
                type="text"
                className="yt-search-input"
                placeholder={getTranslation('search')}
                value={searchQuery}
                autoFocus
                onChange={(e) => setSearchQuery(e.target.value)}
                onKeyDown={handleSearchKeyDown}
                autoComplete="off"
              />
              {searchQuery && (
                <button
                  type="button"
                  className="yt-search-clear-btn"
                  onClick={() => setSearchQuery('')}
                >
                  <MdClose size={18} />
                </button>
              )}
              <button type="submit" className="yt-search-btn" title="Search">
                <MdSearch size={22} />
              </button>

              {/* Suggestions Dropdown for Mobile */}
              {showSuggestions && suggestions.length > 0 && (
                <div className="yt-search-suggestions-dropdown">
                  {suggestions.map((suggestion, index) => {
                    const isSelected = index === selectedSuggestionIndex;
                    return (
                      <div
                        key={`mobile-${suggestion}-${index}`}
                        className={`yt-search-suggestion-item ${isSelected ? 'selected' : ''}`}
                        onMouseDown={(e) => {
                          e.preventDefault();
                          setIsMobileSearchOpen(false);
                          setSearchQuery(suggestion);
                          executeSearch(suggestion);
                        }}
                      >
                        <MdSearch size={18} className="yt-search-suggestion-icon" />
                        <span className="yt-search-suggestion-text">{suggestion}</span>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
            <button
              type="button"
              className="yt-mic-btn"
              onClick={() => setIsVoiceModalOpen(true)}
              title="Voice Search"
            >
              <MdMic size={20} />
            </button>
          </form>
        </div>
      ) : (
        <>
          {/* Left: Hamburger + Logo */}
          <div className="yt-header-left">
        <button
          className="yt-icon-btn"
          onClick={onToggleSidebar}
          title="Toggle Navigation"
          aria-label="Toggle Navigation"
        >
          <MdMenu size={24} />
        </button>

        <a
          href="/"
          className="yt-logo-link"
          title="SpeedoTube Home"
          onClick={(e) => {
            e.preventDefault();
            setSearchQuery('');
            if (onNavigate) onNavigate('/');
          }}
        >
          <div className="yt-logo-wrapper">
            <img src={logoDarkPng} alt="SpeedoTube Logo" className="yt-logo-img dark-logo" />
            <img src={logoLightPng} alt="SpeedoTube Logo" className="yt-logo-img light-logo" />
          </div>
        </a>
      </div>

      {/* Middle: Search Box + Voice Mic */}
      <form className="yt-search-container" onSubmit={handleSearchSubmit}>
        <div className="yt-search-box" ref={searchBoxRef}>
          <input
            ref={searchInputRef}
            type="text"
            className="yt-search-input"
            placeholder={
              activePath === '/history'
                ? 'Search watch history'
                : (activePath === '/profile' || activePath?.startsWith('/studio'))
                ? 'Search across your channel'
                : getTranslation('search')
            }
            value={searchQuery}
            onFocus={() => {
              if (searchQuery.trim() && suggestions.length > 0) {
                setShowSuggestions(true);
              }
            }}
            onChange={(e) => {
              const val = e.target.value;
              setSearchQuery(val);
              if (activePath === '/history') {
                window.dispatchEvent(new CustomEvent('speedotube_search_history', { detail: val }));
              }
            }}
            onKeyDown={handleSearchKeyDown}
            autoComplete="off"
            spellCheck="false"
          />
          {searchQuery && (
            <button
              type="button"
              className="yt-search-clear-btn"
              title="Clear search"
              onClick={() => {
                setSearchQuery('');
                setSuggestions([]);
                setShowSuggestions(false);
                searchInputRef.current?.focus();
              }}
            >
              <MdClose size={18} />
            </button>
          )}
          <button type="submit" className="yt-search-btn" title="Search">
            <MdSearch size={22} />
          </button>

          {/* YouTube-Style Autocomplete Search Suggestions Dropdown */}
          {showSuggestions && suggestions.length > 0 && (
            <div className="yt-search-suggestions-dropdown">
              {suggestions.map((suggestion, index) => {
                const isSelected = index === selectedSuggestionIndex;
                return (
                  <div
                    key={`${suggestion}-${index}`}
                    className={`yt-search-suggestion-item ${isSelected ? 'selected' : ''}`}
                    onMouseDown={(e) => {
                      e.preventDefault();
                      setSearchQuery(suggestion);
                      executeSearch(suggestion);
                    }}
                  >
                    <MdSearch size={18} className="yt-search-suggestion-icon" />
                    <span className="yt-search-suggestion-text">{suggestion}</span>
                  </div>
                );
              })}
            </div>
          )}
        </div>
        <button
          type="button"
          className="yt-mic-btn"
          title="Search with your voice"
          onClick={() => setIsVoiceModalOpen(true)}
        >
          <MdMic size={20} />
        </button>
      </form>

      {/* Right Section: Dynamic Auth State */}
      <div className="yt-header-right">
        {/* Mobile Search Button (only visible on small mobile screens <= 640px) */}
        <button
          type="button"
          className="yt-icon-btn yt-mobile-search-trigger"
          onClick={() => {
            setIsMobileSearchOpen(true);
            setTimeout(() => searchInputRef.current?.focus(), 60);
          }}
          title="Search"
          aria-label="Search"
        >
          <MdSearch size={22} />
        </button>

        {isLoggedIn ? (
          <>
            {/* Create Button + Smooth Dropdown Menu */}
            <div className="yt-menu-relative-wrapper" ref={createMenuRef}>
              <button
                type="button"
                className="yt-create-btn"
                onClick={() => setShowCreateMenu(!showCreateMenu)}
                title="Create"
              >
                <MdAdd size={22} />
                <span>Create</span>
              </button>

              <div className={`yt-dropdown-menu yt-create-dropdown ${showCreateMenu ? 'open' : ''}`}>
                <button
                  type="button"
                  className="yt-dropdown-item"
                  onClick={() => {
                    setShowCreateMenu(false);
                    if (onNavigate) onNavigate('/studio/upload-video');
                  }}
                >
                  <MdOutlineVideocam size={22} className="yt-dropdown-icon" />
                  <span className="yt-dropdown-label">Video</span>
                </button>

                <button
                  type="button"
                  className="yt-dropdown-item"
                  onClick={() => {
                    setShowCreateMenu(false);
                    if (onNavigate) onNavigate('/studio/upload-short');
                  }}
                >
                  <MdOutlineSlowMotionVideo size={22} className="yt-dropdown-icon" />
                  <span className="yt-dropdown-label">Shorts</span>
                </button>

                <button
                  type="button"
                  className="yt-dropdown-item"
                  onClick={() => {
                    setShowCreateMenu(false);
                    if (onNavigate) onNavigate('/studio/upload-album');
                  }}
                >
                  <MdOutlineAlbum size={22} className="yt-dropdown-icon" />
                  <span className="yt-dropdown-label">Music Album</span>
                </button>
              </div>
            </div>

            <button className="yt-icon-btn" title="Notifications">
              <MdOutlineNotifications size={24} />
            </button>

            {/* Logged-In User Profile Avatar + Smooth Dropdown */}
            <div className="yt-menu-relative-wrapper" ref={userMenuRef}>
              <div
                className="yt-user-avatar"
                style={{ backgroundColor: avatarColor }}
                title={profileName}
                onClick={() => {
                  setShowUserMenu(!showUserMenu);
                  setMenuView('main');
                }}
              >
                {avatarUrl && !imgError ? (
                  <img
                    src={avatarUrl}
                    alt={profileName}
                    onError={() => setImgError(true)}
                  />
                ) : (
                  profileInitial
                )}
              </div>

              {/* Smooth Dropdown Menu */}
              <div className={`yt-dropdown-menu ${showUserMenu ? 'open' : ''} ${menuView !== 'main' ? 'lang-view' : ''}`}>
                {menuView === 'language' ? (
                  renderLanguageSubmenu()
                ) : menuView === 'appearance' ? (
                  renderAppearanceSubmenu()
                ) : menuView === 'switch-account' ? (
                  renderSwitchAccountSubmenu()
                ) : (
                  <>
                    <div className="yt-user-info-row">
                      <div className="yt-user-avatar info" style={{ backgroundColor: avatarColor }}>
                        {avatarUrl && !imgError ? (
                          <img
                            src={avatarUrl}
                            alt={profileName}
                            onError={() => setImgError(true)}
                          />
                        ) : (
                          profileInitial
                        )}
                      </div>
                      <div className="yt-user-text">
                        <div className="yt-user-name">{profileName}</div>
                        <div className="yt-user-handle">{profileHandle}</div>
                      </div>
                    </div>

                    <hr className="yt-dropdown-divider" />

                    <button
                      className="yt-dropdown-item"
                      onClick={() => {
                        setShowUserMenu(false);
                        if (onNavigate) onNavigate('/profile');
                      }}
                    >
                      <MdOutlineAccountBox size={22} className="yt-dropdown-icon" />
                      <span className="yt-dropdown-label">
                        {userProfile?.hasChannel === false ? 'Create a channel' : 'View your channel'}
                      </span>
                    </button>

                    <button className="yt-dropdown-item" onClick={() => setMenuView('switch-account')}>
                      <MdSwitchAccount size={22} className="yt-dropdown-icon" />
                      <span className="yt-dropdown-label">Switch account</span>
                      <MdChevronRight size={20} className="yt-dropdown-arrow" />
                    </button>

                    <button className="yt-dropdown-item" onClick={() => setMenuView('appearance')}>
                      <MdOutlineDarkMode size={22} className="yt-dropdown-icon" />
                      <span className="yt-dropdown-label">
                        Appearance: {theme === 'dark' ? 'Dark' : 'Light'}
                      </span>
                      <MdChevronRight size={20} className="yt-dropdown-arrow" />
                    </button>

                    <button className="yt-dropdown-item" onClick={() => setMenuView('language')}>
                      <MdTranslate size={22} className="yt-dropdown-icon" />
                      <span className="yt-dropdown-label">Display language: {selectedLang.name.split(' ')[0]}</span>
                      <MdChevronRight size={20} className="yt-dropdown-arrow" />
                    </button>

                    <button className="yt-dropdown-item" onClick={() => setShowUserMenu(false)}>
                      <MdSettings size={22} className="yt-dropdown-icon" />
                      <span className="yt-dropdown-label">Settings</span>
                    </button>

                    <hr className="yt-dropdown-divider" />

                    <button className="yt-dropdown-item" onClick={handleLogout}>
                      <MdLogout size={22} className="yt-dropdown-icon" />
                      <span className="yt-dropdown-label">Log out</span>
                    </button>
                  </>
                )}
              </div>
            </div>
          </>
        ) : (
          <>
            {/* Pill Black Sign In Button */}
            <button
              className="yt-signin-btn"
              title="Sign in"
              onClick={handleLogin}
            >
              <HiOutlineUserCircle size={24} className="yt-signin-icon" />
              <span>{getTranslation('signIn')}</span>
            </button>

            {/* Three Dots Menu Icon on the RIGHT side of Sign In */}
            <div className="yt-menu-relative-wrapper" ref={moreMenuRef}>
              <button
                className="yt-icon-btn"
                title="Settings"
                onClick={() => {
                  setShowMoreMenu(!showMoreMenu);
                  setMenuView('main');
                }}
              >
                <MdMoreVert size={24} />
              </button>

              {/* More Vert Dropdown Menu */}
              <div className={`yt-dropdown-menu ${showMoreMenu ? 'open' : ''} ${menuView !== 'main' ? 'lang-view' : ''}`}>
                {menuView === 'language' ? (
                  renderLanguageSubmenu()
                ) : menuView === 'appearance' ? (
                  renderAppearanceSubmenu()
                ) : (
                  <>
                    <button className="yt-dropdown-item" onClick={() => setMenuView('appearance')}>
                      <MdOutlineDarkMode size={22} className="yt-dropdown-icon" />
                      <span className="yt-dropdown-label">
                        Appearance: {theme === 'dark' ? 'Dark' : 'Light'}
                      </span>
                      <MdChevronRight size={20} className="yt-dropdown-arrow" />
                    </button>

                    <button className="yt-dropdown-item" onClick={() => setMenuView('language')}>
                      <MdTranslate size={22} className="yt-dropdown-icon" />
                      <span className="yt-dropdown-label">{getTranslation('displayLanguage')}: {selectedLang.name.split(' ')[0]}</span>
                      <MdChevronRight size={20} className="yt-dropdown-arrow" />
                    </button>

                    <button className="yt-dropdown-item" onClick={() => setShowMoreMenu(false)}>
                      <MdSettings size={22} className="yt-dropdown-icon" />
                      <span className="yt-dropdown-label">{getTranslation('settings')}</span>
                    </button>

                    <button className="yt-dropdown-item" onClick={() => setShowMoreMenu(false)}>
                      <MdOutlineHelpOutline size={22} className="yt-dropdown-icon" />
                      <span className="yt-dropdown-label">{getTranslation('help')}</span>
                    </button>
                  </>
                )}
              </div>
            </div>
          </>
        )}
      </div>
        </>
      )}

      {/* Voice Search Creative Humming Modal */}
      <VoiceSearchModal
        isOpen={isVoiceModalOpen}
        onClose={() => setIsVoiceModalOpen(false)}
        onSearch={(spokenText) => {
          setSearchQuery(spokenText);
          executeSearch(spokenText);
        }}
      />

      {/* Logout Confirmation Modal */}
      <ConfirmModal
        isOpen={showLogoutModal}
        title="Log out"
        message="Are you sure you want to log out of SpeedoTube?"
        confirmText="Log out"
        cancelText="Cancel"
        icon={MdLogout}
        danger={true}
        onConfirm={handleConfirmLogout}
        onClose={() => setShowLogoutModal(false)}
      />
    </header>
  );
}
